import {FC, useMemo, useState} from 'react'
import {PrintStoryEvent, PrintStoryLane} from '../types'
import {formatRelative, formatTimestamp, sortEventsDescending} from '../time'
import {SeverityBadge} from './SeverityBadge'

type Props = {
  events: PrintStoryEvent[]
  onSelectEvent?: (event: PrintStoryEvent) => void
  emptyText?: string
}

const LANE_FILTERS: Array<{value: PrintStoryLane | 'all'; label: string}> = [
  {value: 'all', label: 'All'},
  {value: 'printer_health', label: 'Health'},
  {value: 'prefetch', label: 'Prefetch'},
  {value: 'print', label: 'Print'},
  {value: 'callback', label: 'Callback'},
  {value: 'connection', label: 'Connection'},
  {value: 'content', label: 'Content'},
  {value: 'code', label: 'Code'},
  {value: 'render', label: 'Render'},
  {value: 'mail', label: 'Mail'},
]

export const StoryEventFeed: FC<Props> = ({
  events,
  onSelectEvent,
  emptyText = 'No events in this window.',
}) => {
  const [laneFilter, setLaneFilter] = useState<PrintStoryLane | 'all'>('all')

  const filtered = useMemo(() => {
    const list =
      laneFilter === 'all' ? events : events.filter((event) => event.lane === laneFilter)
    return sortEventsDescending(list)
  }, [events, laneFilter])

  return (
    <div>
      <div className='d-flex flex-wrap gap-2 mb-4'>
        {LANE_FILTERS.map((filter) => (
          <button
            key={filter.value}
            type='button'
            className={`btn btn-sm ${
              laneFilter === filter.value ? 'btn-primary' : 'btn-light'
            }`}
            onClick={() => setLaneFilter(filter.value)}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className='text-muted fw-semibold'>{emptyText}</div>
      ) : (
        <div className='d-flex flex-column gap-3'>
          {filtered.map((event, index) => (
            <div
              key={`${event.kind}-${event.ts}-${event.jobId || event.deviceId || index}`}
              className='border border-dashed rounded p-4'
            >
              <div className='d-flex justify-content-between align-items-start gap-3'>
                <div>
                  <div className='d-flex align-items-center gap-2 mb-1 flex-wrap'>
                    <SeverityBadge severity={event.severity}>{event.lane}</SeverityBadge>
                    <span className='fw-bold text-gray-800'>{event.title}</span>
                  </div>
                  {event.detail && (
                    <div className='text-muted fs-7 mb-1'>{event.detail}</div>
                  )}
                  <div className='text-muted fs-8'>
                    {formatRelative(event.ts)} · {formatTimestamp(event.ts)}
                    {event.deviceId ? ` · ${event.deviceId}` : ''}
                    {event.jobId ? ` · job ${event.jobId}` : ''}
                  </div>
                </div>
                {onSelectEvent && (
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary'
                    onClick={() => onSelectEvent(event)}
                  >
                    JSON
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
